import React from 'react';
import {
  MapPin,
  Camera,
  UserCircle,
  ShieldCheck,
  CreditCard,
  Scale,
  Mail,
  Globe,
  FileText,
  UserCheck,
} from 'lucide-react';

interface Section {
  id: string;
  title: string;
  icon: React.ElementType;
  highlight?: boolean;
  body: React.ReactNode;
}

const sections: Section[] = [
  {
    id: 'introduction',
    title: 'Introduction',
    icon: FileText,
    body: (
      <p className="text-slate-600 leading-relaxed">
        Welcome to Adroj ("Platform"), an outdoor advertising marketplace connecting Space
        Providers, Brand Companies, and Media Service Providers. By accessing or using our
        mobile application, you agree to comply with these Terms and Conditions and our
        Privacy Policy.
      </p>
    ),
  },
  {
    id: 'eligibility',
    title: 'Eligibility & Account Registration',
    icon: UserCheck,
    body: (
      <p className="text-slate-600 leading-relaxed">
        Users must be at least 18 years old. Users are required to provide valid and truthful
        information during registration, including mobile numbers for OTP authentication and
        official identification documents for KYC verification. Adroj reserves the right to
        suspend or terminate accounts that violate platform policies.
      </p>
    ),
  },
  {
    id: 'permissions',
    title: 'Mobile Application Permissions & Data Collection',
    icon: ShieldCheck,
    highlight: true,
    body: (
      <>
        <p className="text-slate-700 font-medium leading-relaxed mb-5">
          To ensure seamless core functionality and maintain system compliance, the Adroj
          Android application collects and processes the following device scopes:
        </p>
        <div className="space-y-4">
          <div className="flex gap-3">
            <div className="mt-0.5 shrink-0 w-8 h-8 rounded-lg bg-white flex items-center justify-center border border-orange-200">
              <MapPin className="w-4 h-4 text-orange-600" strokeWidth={2} />
            </div>
            <p className="text-slate-600 leading-relaxed">
              <span className="text-slate-900 font-semibold">
                Precise &amp; Approximate Location Data:
              </span>{' '}
              Collected exclusively from operational Media Service Providers to enable
              interactive Google Maps navigation, dynamic service area selection, and
              geographic job site check-ins.
            </p>
          </div>
          <div className="flex gap-3">
            <div className="mt-0.5 shrink-0 w-8 h-8 rounded-lg bg-white flex items-center justify-center border border-orange-200">
              <Camera className="w-4 h-4 text-orange-600" strokeWidth={2} />
            </div>
            <p className="text-slate-600 leading-relaxed">
              <span className="text-slate-900 font-semibold">Camera and Storage Access:</span>{' '}
              Required strictly for Space Providers to upload real-time advertisement space
              photos, and for Media Service Providers to capture and upload mandatory "Before
              &amp; After" structural proof-of-performance execution media.
            </p>
          </div>
          <div className="flex gap-3">
            <div className="mt-0.5 shrink-0 w-8 h-8 rounded-lg bg-white flex items-center justify-center border border-orange-200">
              <UserCircle className="w-4 h-4 text-orange-600" strokeWidth={2} />
            </div>
            <p className="text-slate-600 leading-relaxed">
              <span className="text-slate-900 font-semibold">Personal Identifiers:</span> We
              securely collect profile configurations including names, email addresses, and
              phone networks to authorize encrypted validation operations utilizing customized
              OTP pathways.
            </p>
          </div>
        </div>
      </>
    ),
  },
  {
    id: 'data-security',
    title: 'Data Use & Security Protocols',
    icon: ShieldCheck,
    body: (
      <p className="text-slate-600 leading-relaxed">
        All processed telemetry metrics, document arrays, and operational image layers flow
        natively through heavily encrypted transmission layers. Structured elements deploy
        safely behind secure PostgreSQL infrastructures and distributed remote clusters (AWS
        S3/Cloudinary ecosystem). Location streams and contextual asset proof listings maintain
        zero visibility to external marketing clusters and are deployed solely to confirm
        validation thresholds.
      </p>
    ),
  },
  {
    id: 'financial',
    title: 'Financial Processing Systems',
    icon: CreditCard,
    body: (
      <p className="text-slate-600 leading-relaxed">
        All electronic commerce transactions, rental ledger distributions, processing metrics,
        and structural provider execution pay-sheets process transparently through
        industry-standard API frameworks utilizing unified secure gateway pipelines (Razorpay).
      </p>
    ),
  },
  {
    id: 'governing-law',
    title: 'Governing Law & Operational Rights',
    icon: Scale,
    body: (
      <p className="text-slate-600 leading-relaxed">
        Registered internal operators retain absolute capability to adjust, update, or
        completely delete stored profiles or background processing assets at any time.
        Regulatory oversight and framework enforcement adhere structurally to the legal codes
        and judicial baselines of India.
      </p>
    ),
  },
];

const PrivacyPolicy: React.FC = () => {
  const currentYear = new Date().getFullYear();

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="bg-white font-sans">
      {/* Hero */}
      <div className="bg-[#f8f6f1] border-b border-[#e8e2d9]">
        <div className="max-w-4xl mx-auto px-6 py-16 md:py-20">
          <span className="text-xs font-bold tracking-widest text-orange-600 uppercase">
            Adroj
          </span>
          <h1 className="mt-2 text-3xl md:text-5xl font-extrabold text-slate-900 tracking-tight">
            Privacy Policy &amp; Terms of Service
          </h1>
          <p className="mt-3 text-slate-500">
            Last updated July 2026 · Applies to the Adroj mobile application
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-10 md:gap-16">
          {/* Sticky in-page nav */}
          <nav className="hidden md:block sticky top-6 self-start">
            <p className="text-xs font-bold tracking-wider text-slate-400 mb-3">
              ON THIS PAGE
            </p>
            <div className="flex flex-col gap-2">
              {sections.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => scrollTo(s.id)}
                  className="text-left text-sm text-slate-500 hover:text-orange-600 transition-colors leading-snug"
                >
                  {i + 1}. {s.title}
                </button>
              ))}
              <button
                onClick={() => scrollTo('contact')}
                className="text-left text-sm text-slate-500 hover:text-orange-600 transition-colors leading-snug"
              >
                {sections.length + 1}. Contact Information
              </button>
            </div>
          </nav>

          {/* Sections */}
          <div className="flex flex-col gap-12 md:gap-14">
            {sections.map((section, i) => {
              const Icon = section.icon;
              return (
                <section
                  key={section.id}
                  id={section.id}
                  className={
                    'scroll-mt-6' +
                    (section.highlight
                      ? ' bg-orange-50/60 border border-orange-100 rounded-2xl p-6 md:p-8'
                      : '')
                  }
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
                      <Icon className="w-4.5 h-4.5 text-orange-600" strokeWidth={2} />
                    </div>
                    <h2 className="text-xl md:text-2xl font-bold text-slate-900">
                      <span className="text-orange-600 mr-2">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      {section.title}
                    </h2>
                  </div>
                  {section.body}
                </section>
              );
            })}

            {/* Contact */}
            <section id="contact" className="scroll-mt-6 border-t border-slate-200 pt-10">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 rounded-full bg-orange-100 flex items-center justify-center shrink-0">
                  <Mail className="w-4.5 h-4.5 text-orange-600" strokeWidth={2} />
                </div>
                <h2 className="text-xl md:text-2xl font-bold text-slate-900">
                  <span className="text-orange-600 mr-2">
                    {String(sections.length + 1).padStart(2, '0')}
                  </span>
                  Contact Information
                </h2>
              </div>
              <p className="text-slate-600 leading-relaxed">
                For administrative issues, programmatic feedback, or manual deletion requests,
                feel free to reach our team directly:
              </p>
              <div className="mt-4 flex flex-col gap-2">
                <a
                  href="mailto:support@adroj.com"
                  className="inline-flex items-center gap-2 text-slate-900 font-medium hover:text-orange-600 transition-colors w-fit"
                >
                  <Mail className="w-4 h-4 text-orange-600" strokeWidth={2} />
                  support@adroj.com
                </a>
                <a
                  href="https://www.adroj.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-slate-900 font-medium hover:text-orange-600 transition-colors w-fit"
                >
                  <Globe className="w-4 h-4 text-orange-600" strokeWidth={2} />
                  www.adroj.com
                </a>
              </div>
            </section>
          </div>
        </div>

        <div className="border-t border-slate-100 mt-16 pt-6 text-center">
          <p className="text-xs text-slate-400">© {currentYear} Adroj. All Rights Reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;